import { Grid } from "@mui/material";
import { logger } from "@platform/browser-logger";
import { useFieldDependencies } from "@platform/hooks";
import { JSX } from "react";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";

import { FieldComponent, FieldConfig } from "./FormComponents";
import { useFormGeneratorContext } from "./FormGeneratorContext";

interface FieldElementProps<
  TCustomType extends string,
  Values extends FieldValues,
> {
  field: FieldConfig<TCustomType>;
  methods: UseFormReturn<Values>;
  parentPath?: string;
}

export const FieldElement = <
  TCustomType extends string,
  Values extends FieldValues,
>({
  field,
  methods,
  parentPath = "",
}: FieldElementProps<TCustomType, Values>): JSX.Element | null => {
  const { componentMap } = useFormGeneratorContext();
  const formState = useWatch({ control: methods.control });

  const name = parentPath ? `${parentPath}.${field.name}` : field.name;
  // for some reason TS is not inferring the type correctly when { ...field, name } is used
  const modifiedField = { ...field };
  modifiedField.name = name;

  const { isVisible, isDisabled, overrideValue, overrideOptions } =
    useFieldDependencies(modifiedField, formState);

  if (!isVisible) return null;

  const { grid, type } = field;
  const Component = componentMap[
    type as keyof typeof componentMap
  ] as FieldComponent<TCustomType, Values>;

  if (!Component) {
    logger.warn(`No component registered for type: ${type}`);
    return null;
  }

  const overriddenField = {
    ...field,
    name,
    disabled: isDisabled || field.disabled,
    defaultValue: overrideValue ?? field.defaultValue,
    options: overrideOptions ?? field.options,
    sx: {
      fontWeight: field.type === "sectionHeader" ? 600 : "normal",
      fontSize: "1rem",
      ...field.sx,
    },
  };

  return (
    <Grid {...grid} key={name} data-testid={`ds-form-field-${name}`}>
      <Component field={overriddenField} methods={methods} />
    </Grid>
  );
};
